import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Ruler, ScanFace } from "lucide-react";
import { UNDERTONES, TEXTURES, LENGTHS, SHAPES, cap } from "@/components/onboarding/metricsOptions";

const MetricsForm = ({ metricsForm, setMetricsForm }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <div className="space-y-1.5">
      <Label>Height (cm)</Label>
      <Input data-testid="onboarding-metrics-height-input" type="number" value={metricsForm.height_cm}
        onChange={(e) => setMetricsForm({ ...metricsForm, height_cm: e.target.value })} className="rounded-xl bg-card" />
    </div>
    <div className="space-y-1.5">
      <Label>Skin undertone</Label>
      <Select value={metricsForm.skin_undertone} onValueChange={(v) => setMetricsForm({ ...metricsForm, skin_undertone: v })}>
        <SelectTrigger data-testid="onboarding-metrics-undertone-select" className="rounded-xl bg-card"><SelectValue /></SelectTrigger>
        <SelectContent>{UNDERTONES.map((u) => <SelectItem key={u} value={u}>{cap(u)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label>Hair texture</Label>
      <Select value={metricsForm.hair_texture} onValueChange={(v) => setMetricsForm({ ...metricsForm, hair_texture: v })}>
        <SelectTrigger data-testid="onboarding-metrics-texture-select" className="rounded-xl bg-card"><SelectValue /></SelectTrigger>
        <SelectContent>{TEXTURES.map((t) => <SelectItem key={t} value={t}>{cap(t)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label>Hair length</Label>
      <Select value={metricsForm.hair_length} onValueChange={(v) => setMetricsForm({ ...metricsForm, hair_length: v })}>
        <SelectTrigger data-testid="onboarding-metrics-length-select" className="rounded-xl bg-card"><SelectValue /></SelectTrigger>
        <SelectContent>{LENGTHS.map((l) => <SelectItem key={l} value={l}>{cap(l)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label>Hair colour</Label>
      <Input data-testid="onboarding-metrics-hair-color-input" value={metricsForm.hair_color}
        onChange={(e) => setMetricsForm({ ...metricsForm, hair_color: e.target.value })} className="rounded-xl bg-card" />
    </div>
    <div className="space-y-1.5">
      <Label>Eye colour</Label>
      <Input data-testid="onboarding-metrics-eye-color-input" value={metricsForm.eye_color}
        onChange={(e) => setMetricsForm({ ...metricsForm, eye_color: e.target.value })} className="rounded-xl bg-card" />
    </div>
    <div className="space-y-1.5 sm:col-span-2">
      <Label>Body shape</Label>
      <Select value={metricsForm.body_shape} onValueChange={(v) => setMetricsForm({ ...metricsForm, body_shape: v })}>
        <SelectTrigger data-testid="onboarding-metrics-shape-select" className="rounded-xl bg-card"><SelectValue /></SelectTrigger>
        <SelectContent>{SHAPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </div>
);

export const MetricsStep = ({
  ob, checklist, extracting, confirming, metricsForm, setMetricsForm,
  onExtract, onConfirm, setStep,
}) => {
  const ex = ob?.metadata?.extracted;
  const idStatus = checklist.id_status;
  return (
    <Card data-testid="onboarding-metrics-step" className="mt-6 rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Ruler className="h-4 w-4" /> AI-extracted metrics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!ex ? (
          <>
            <p className="text-sm text-muted-foreground">
              Our AI reads your completed dataset and extracts the physical attributes clients search by —
              skin undertone, hair texture, body shape and more. You review and confirm every value before
              anything is published.
            </p>
            <Button data-testid="onboarding-extract-button" disabled={extracting || !checklist.dataset_done} onClick={onExtract} className="rounded-xl">
              {extracting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is analysing your dataset… (30–60s)</>
              ) : (
                <><ScanFace className="mr-2 h-4 w-4" /> Run AI analysis</>
              )}
            </Button>
            {!checklist.dataset_done && (
              <p className="text-[11px] text-muted-foreground">Complete the 3 required reference photos first.</p>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-xl border bg-muted/50 p-4 text-sm">
              <ScanFace className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(174,38%,34%)]" />
              <div>
                <p className="font-semibold">AI analysis {checklist.metadata_confirmed ? "confirmed" : "ready for your review"}</p>
                <p className="text-muted-foreground">
                  {ex.notes || "Review each value below and correct anything the AI got wrong — you have the final say."}
                  {typeof ex.confidence === "number" && ` (AI confidence ${Math.round(ex.confidence * 100)}%)`}
                </p>
                {ex.estimated_height_range_cm && (
                  <p className="mt-1 font-mono-plex text-xs text-muted-foreground">AI height estimate: {ex.estimated_height_range_cm} cm</p>
                )}
              </div>
            </div>

            {metricsForm && <MetricsForm metricsForm={metricsForm} setMetricsForm={setMetricsForm} />}

            <div className="flex flex-wrap items-center gap-3">
              <Button data-testid="onboarding-confirm-button" disabled={confirming || !metricsForm} onClick={onConfirm} className="rounded-xl">
                {confirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {checklist.metadata_confirmed ? "Update confirmed metrics" : "Confirm metrics & publish"}
              </Button>
              <Button variant="outline" data-testid="onboarding-reextract-button" disabled={extracting} onClick={onExtract}
                className="rounded-xl border bg-card hover:bg-muted">
                {extracting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Re-run AI analysis
              </Button>
            </div>
            {!checklist.complete && checklist.metadata_confirmed && (
              <p className="text-[11px] text-muted-foreground">
                Metrics confirmed — finish {idStatus !== "verified" ? "ID verification" : "your dataset"} to go live.
              </p>
            )}
          </>
        )}
        <div className="flex justify-start border-t pt-4">
          <Button variant="ghost" className="rounded-xl" onClick={() => setStep(2)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Government ID
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
